import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ShieldCheck, Search, FileCheck, TrendingUp } from 'lucide-react';

export const DueDiligenceSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const checks = [
    { icon: <FileCheck className="w-6 h-6" />, text: "Legal titles and documentation verification" },
    { icon: <TrendingUp className="w-6 h-6" />, text: "Financial viability assessment" },
    { icon: <ShieldCheck className="w-6 h-6" />, text: "Operational soundness evaluation" },
    { icon: <Search className="w-6 h-6" />, text: "Counterparty reliability checks" },
  ];

  return (
    <section id="due-diligence" ref={containerRef} className="py-24 bg-zinc-50">
      <div className="container mx-auto px-6">
        <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
          
          {/* Image Side */}
          <motion.div 
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-amber-500 transform translate-x-4 translate-y-4 rounded-sm" />
              <img 
                src="https://public.youware.com/users-website-assets/prod/5cdbe780-871f-42f1-8d67-aecb910eea07/e50b80e6f73f49899d828ee4908d1abc.jpg" 
                alt="Due Diligence Analysis" 
                className="relative z-10 w-full rounded-sm shadow-lg grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div 
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h4 className="text-amber-500 font-bold uppercase tracking-widest text-sm mb-2">Risk Management</h4>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
              Rigorous, Proprietary <br/>Due Diligence
            </h2>
            <p className="text-zinc-600 text-lg mb-8 leading-relaxed">
              Every mandate begins with strict, proprietary due diligence. We believe that transparency and technical insight are the keys to unlocking value. This disciplined approach ensures alignment with the expectations and risk profiles of both our clients and our capital partners.
            </p>

            <div className="grid gap-6">
              {checks.map((check, index) => (
                <motion.div 
                  key={index}
                  className="flex items-center bg-white p-4 rounded-sm shadow-sm border-l-4 border-zinc-900"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                >
                  <div className="text-amber-500 mr-4">{check.icon}</div>
                  <div className="font-medium text-zinc-800">{check.text}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};